import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, animate } from "framer-motion";
import { fadeUp, staggerContainer, easeOut } from "@/lib/motion";
import { cn } from "@/lib/utils";

/* ---------------- Scroll reveal ---------------- */
export const Reveal = ({ children, className, delay = 0, y = 18 }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, ease: easeOut, delay }}>
      {children}
    </motion.div>
  );
};

/* ---------------- Staggered group ---------------- */
export const StaggerGroup = ({ children, className, stagger = 0.07 }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} variants={staggerContainer(stagger)} initial="hidden"
      whileInView="show" viewport={{ once: true, margin: "-40px" }}>
      {children}
    </motion.div>
  );
};
export const StaggerItem = ({ children, className }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return <motion.div className={className} variants={fadeUp}>{children}</motion.div>;
};

/* ---------------- Animated counter ---------------- */
export const AnimatedCounter = ({ value = 0, decimals = 0, prefix = "", suffix = "", className, duration = 1.1 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setDisplay(value); return; }
    const controls = animate(0, Number(value) || 0, {
      duration, ease: easeOut,
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value, reduce, duration]);
  return (
    <span ref={ref} className={className}>
      {prefix}{Number(display).toLocaleString("fr-CA", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}
    </span>
  );
};

/* ---------------- Progress ring ---------------- */
export const ProgressRing = ({ value = 0, max = 100, size = 92, stroke = 9, color = "hsl(var(--secondary))", track = "hsl(var(--muted))", children }) => {
  const reduce = useReducedMotion();
  const pct = max > 0 ? Math.min(1, value / max) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const target = c * (1 - pct);
  return (
    <div ref={ref} className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: reduce ? target : c }}
          animate={{ strokeDashoffset: inView ? target : c }}
          transition={{ duration: reduce ? 0 : 1.1, ease: easeOut }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

/* ---------------- Progress bar ---------------- */
export const ProgressBar = ({ value = 0, max = 100, className, barClassName }) => {
  const reduce = useReducedMotion();
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div ref={ref} className={cn("h-2.5 w-full overflow-hidden rounded-full bg-muted", className)}>
      <motion.div className={cn("h-full rounded-full bg-secondary", barClassName)}
        initial={{ width: reduce ? `${pct}%` : 0 }} animate={{ width: inView ? `${pct}%` : 0 }}
        transition={{ duration: reduce ? 0 : 1, ease: easeOut }} />
    </div>
  );
};

/* ---------------- Skeletons ---------------- */
export const Skeleton = ({ className }) => <div className={cn("asl-skeleton rounded-md", className)} />;
export const SkeletonCard = () => (
  <div className="rounded-2xl border bg-card p-5 space-y-3">
    <Skeleton className="h-32 w-full" />
    <Skeleton className="h-4 w-2/3" />
    <Skeleton className="h-3 w-1/2" />
  </div>
);
export const SkeletonGrid = ({ count = 6, cols = "sm:grid-cols-2 lg:grid-cols-3" }) => (
  <div className={cn("grid grid-cols-1 gap-4", cols)}>{Array.from({ length: count }).map((_, i) => <SkeletonCard key={i} />)}</div>
);
export const SkeletonList = ({ count = 4 }) => (
  <div className="space-y-3">{Array.from({ length: count }).map((_, i) => (
    <div key={i} className="flex items-center gap-3 rounded-2xl border bg-card p-4">
      <Skeleton className="h-11 w-11 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-3 w-1/2" /></div>
    </div>
  ))}</div>
);

/* ---------------- Lazy image with fade-in ---------------- */
export const LazyImage = ({ src, alt = "", className, imgClassName }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={cn("relative overflow-hidden bg-muted", className)}>
      {!loaded && <div className="asl-skeleton absolute inset-0" />}
      {src && (
        <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setLoaded(true)}
          className={cn("h-full w-full object-cover transition-opacity duration-500", loaded ? "opacity-100" : "opacity-0", imgClassName)} />
      )}
    </div>
  );
};

/* ---------------- Sport cover with default imagery ---------------- */
const COVERS = {
  soccer: "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?auto=format&fit=crop&w=900&q=60",
  foot: "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?auto=format&fit=crop&w=900&q=60",
  basket: "https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&w=900&q=60",
  gym: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=900&q=60",
  hockey: "https://images.unsplash.com/photo-1580692475446-c2fabbbca4c8?auto=format&fit=crop&w=900&q=60",
  volley: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?auto=format&fit=crop&w=900&q=60",
  tennis: "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&w=900&q=60",
  natation: "https://images.unsplash.com/photo-1530549387789-4c1017266635?auto=format&fit=crop&w=900&q=60",
  course: "https://images.unsplash.com/photo-1571008887538-b36bb32f4571?auto=format&fit=crop&w=900&q=60",
  danse: "https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?auto=format&fit=crop&w=900&q=60",
  baseball: "https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&w=900&q=60",
};
const GENERIC = "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&w=900&q=60";

export function coverFor(sport) {
  if (sport?.cover_image) return sport.cover_image;
  const n = (sport?.name || "").toLowerCase();
  for (const key of Object.keys(COVERS)) if (n.includes(key)) return COVERS[key];
  return GENERIC;
}

export const SportCover = ({ sport, className, children }) => (
  <div className={cn("relative overflow-hidden", className)}>
    <LazyImage src={coverFor(sport)} alt={sport?.name} className="absolute inset-0 h-full w-full" imgClassName="transition-transform duration-500 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/35 to-transparent" />
    <div className="relative">{children}</div>
  </div>
);

/* ---------------- One-shot confetti ---------------- */
const COLORS = ["#3faa5a", "#e0b13a", "#221f4b", "#ffffff", "#4caf50"];
export const ConfettiBurst = ({ fire = false, pieces = 42 }) => {
  const reduce = useReducedMotion();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (fire && !reduce) { setShow(true); const t = setTimeout(() => setShow(false), 2200); return () => clearTimeout(t); }
  }, [fire, reduce]);
  if (!show) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden">
      {Array.from({ length: pieces }).map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.25;
        const rot = Math.random() * 360;
        const color = COLORS[i % COLORS.length];
        const size = 6 + Math.random() * 6;
        return (
          <motion.span key={i} style={{ left: `${left}%`, top: "-6%", width: size, height: size * 1.4, background: color, borderRadius: 2 }}
            className="absolute" initial={{ y: 0, opacity: 1, rotate: 0 }}
            animate={{ y: "120%", opacity: [1, 1, 0], rotate: rot }}
            transition={{ duration: 1.6 + Math.random() * 0.6, delay, ease: "easeOut" }} />
        );
      })}
    </div>
  );
};

// Helpers to fire confetti only once per new item id
export function shouldCelebrate(key, currentIds = []) {
  try {
    const seen = JSON.parse(localStorage.getItem(key) || "[]");
    const fresh = currentIds.filter((id) => id && !seen.includes(id));
    if (fresh.length) {
      localStorage.setItem(key, JSON.stringify(Array.from(new Set([...seen, ...currentIds])).slice(-200)));
      return true;
    }
    // still persist current ids
    localStorage.setItem(key, JSON.stringify(Array.from(new Set([...seen, ...currentIds])).slice(-200)));
    return false;
  } catch { return false; }
}
